import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, FileText, Upload, ArrowRight, Sparkles, Trash2, Plus, Building2, DollarSign } from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailToQuote() {
  var token = localStorage.getItem('nbe_token');
  var navigate = useNavigate();
  var [emailText, setEmailText] = useState('');
  var [file, setFile] = useState(null);
  var [extracting, setExtracting] = useState(false);
  var [extracted, setExtracted] = useState(null);
  var [creating, setCreating] = useState(false);

  // Rate card state
  var [rateCards, setRateCards] = useState([]);
  var [selectedCard, setSelectedCard] = useState(null);
  var [rateCardDetail, setRateCardDetail] = useState(null);
  var [selectedQuarter, setSelectedQuarter] = useState('Q1');
  var [appliedRates, setAppliedRates] = useState(null);

  // Broker commission state
  var [commType, setCommType] = useState('percent'); // 'percent' or 'ckwh'
  var [commValue, setCommValue] = useState('');

  useEffect(function() {
    if (token) {
      axios.get(API_URL + '/api/ci-rate-cards?authorization=' + token)
        .then(function(r) { setRateCards(r.data || []); })
        .catch(function() {});
    }
  }, [token]);

  function extract() {
    if (!emailText.trim() && !file) { toast.error('Paste an email or upload a file'); return; }
    setExtracting(true);
    var fd = new FormData();
    fd.append('email_text', emailText);
    if (file) fd.append('file', file);
    axios.post(API_URL + '/api/email-to-quote/extract?authorization=' + token, fd)
      .then(function(r) {
        setExtracted(r.data.extracted);
        toast.success('Data extracted successfully');
      })
      .catch(function(err) { toast.error(err.response?.data?.detail || 'Extraction failed'); })
      .finally(function() { setExtracting(false); });
  }

  function updateField(field, value) {
    setExtracted(function(prev) { return Object.assign({}, prev, { [field]: value }); });
  }

  function updateAddress(idx, field, value) {
    setExtracted(function(prev) {
      var addrs = (prev.supply_addresses || []).map(function(a, i) {
        return i === idx ? Object.assign({}, a, { [field]: value }) : a;
      });
      return Object.assign({}, prev, { supply_addresses: addrs });
    });
  }

  function addAddress() {
    setExtracted(function(prev) {
      var addrs = (prev.supply_addresses || []).concat([{ supply_address: '', suburb: '', state: '', postcode: '', nmi: '', estimated_pk: '', estimated_opk: '', estimated_shoulder: '', estimated_kwh: '', tariff: '' }]);
      return Object.assign({}, prev, { supply_addresses: addrs });
    });
  }

  function removeAddress(idx) {
    setExtracted(function(prev) {
      var addrs = (prev.supply_addresses || []).filter(function(_, i) { return i !== idx; });
      return Object.assign({}, prev, { supply_addresses: addrs });
    });
  }

  function loadRateCard(cardId) {
    setSelectedCard(cardId);
    setAppliedRates(null);
    if (!cardId) { setRateCardDetail(null); return; }
    axios.get(API_URL + '/api/ci-rate-cards/' + cardId + '?authorization=' + token)
      .then(function(r) { setRateCardDetail(r.data); })
      .catch(function() { toast.error('Failed to load rate card'); });
  }

  function getStateKey() {
    if (!extracted) return null;
    var cs = extracted.contract_state || 'QLD_SA';
    if (cs === 'NSW') return 'NSW';
    if (cs === 'VIC') return 'VIC';
    if (rateCardDetail && rateCardDetail.data && rateCardDetail.data.quarters) {
      var q = rateCardDetail.data.quarters[selectedQuarter] || {};
      var er = q.energy_rates || {};
      return er['QLD'] ? 'QLD' : (er['SA'] ? 'SA' : null);
    }
    return 'QLD';
  }

  function applyRates() {
    if (!rateCardDetail || !rateCardDetail.data) { toast.error('No rate card loaded'); return; }
    var d = rateCardDetail.data;
    var q = d.quarters && d.quarters[selectedQuarter];
    if (!q) { toast.error('No data for ' + selectedQuarter); return; }
    var stateKey = getStateKey();
    if (!stateKey || !q.energy_rates[stateKey]) { toast.error('No rates for state'); return; }

    var rates = q.energy_rates[stateKey];
    var env = d.environmental || {};
    var met = d.metering || {};
    var stepped = rates.stepped || {};
    var smoothed = rates.smoothed || {};

    // Build applied rates object
    var applied = { stepped: {}, smoothed: {}, env: {}, metering: {}, service_charge: null };
    for (var y = 1; y <= 3; y++) {
      if (stepped[String(y)]) {
        applied.stepped[y] = Object.assign({}, stepped[String(y)]);
      }
    }
    for (var s = 2; s <= 3; s++) {
      if (smoothed[String(s)]) {
        applied.smoothed[s] = Object.assign({}, smoothed[String(s)]);
      }
    }
    applied.env = { LREC: env['LREC'] || {}, SREC: env['SREC'] || {}, VEEC: env['VEEC'] || {}, ESC: env['ESC'] || {} };
    applied.metering = met;
    var anyRate = stepped['1'] || smoothed['2'] || {};
    applied.service_charge = anyRate.service_charge;
    applied.stateKey = stateKey;

    setAppliedRates(applied);
    toast.success('Rates loaded from rate card (' + selectedQuarter + ', ' + stateKey + ')');
  }

  function applyCommission(rate) {
    if (!commValue || parseFloat(commValue) === 0) return rate;
    var r = parseFloat(rate);
    if (isNaN(r)) return rate;
    var cv = parseFloat(commValue);
    if (commType === 'percent') {
      return Math.round((r + r * cv / 100) * 10000) / 10000;
    } else {
      return Math.round((r + cv) * 10000) / 10000;
    }
  }

  function createContract() {
    setCreating(true);
    var d = extracted;
    var state = d.contract_state || 'QLD_SA';

    // Build pricing from applied rates + commission
    var pricingType = d.pricing_type || 'flat';
    // Normalize to match ContractGenerator expectations
    if (pricingType !== 'stepped' && pricingType !== 'smoothed') {
      if (pricingType.startsWith('smooth')) pricingType = 'smoothed';
      else pricingType = 'stepped';
    }
    var pricing = { offer_type: pricingType };
    if (appliedRates) {
      var st = appliedRates.stepped || {};
      if (st[1]) { pricing.step_y1_peak = String(applyCommission(st[1].peak || 0)); pricing.step_y1_shoulder = String(applyCommission(st[1].shoulder || 0)); pricing.step_y1_offpeak = String(applyCommission(st[1].offpeak || 0)); }
      if (st[2]) { pricing.step_y2_peak = String(applyCommission(st[2].peak || 0)); pricing.step_y2_shoulder = String(applyCommission(st[2].shoulder || 0)); pricing.step_y2_offpeak = String(applyCommission(st[2].offpeak || 0)); }
      if (st[3]) { pricing.step_y3_peak = String(applyCommission(st[3].peak || 0)); pricing.step_y3_shoulder = String(applyCommission(st[3].shoulder || 0)); pricing.step_y3_offpeak = String(applyCommission(st[3].offpeak || 0)); }
      var sm = appliedRates.smoothed || {};
      if (sm[2]) { pricing.smooth_24_peak = String(applyCommission(sm[2].peak || 0)); pricing.smooth_24_shoulder = String(applyCommission(sm[2].shoulder || 0)); pricing.smooth_24_offpeak = String(applyCommission(sm[2].offpeak || 0)); }
      if (sm[3]) { pricing.smooth_36_peak = String(applyCommission(sm[3].peak || 0)); pricing.smooth_36_shoulder = String(applyCommission(sm[3].shoulder || 0)); pricing.smooth_36_offpeak = String(applyCommission(sm[3].offpeak || 0)); }
    }
    // Set pricing schedule start dates from the Contract Start Date
    var startDateRaw = d.proposed_transfer_date || '';
    if (startDateRaw) {
      // Parse DD/MM/YYYY format
      var parts = startDateRaw.split('/');
      if (parts.length === 3) {
        var day = parseInt(parts[0], 10);
        var month = parseInt(parts[1], 10) - 1;
        var year = parseInt(parts[2], 10);
        var baseDate = new Date(year, month, day);
        if (!isNaN(baseDate.getTime())) {
          var fmtDate = function(dt) {
            var dd = String(dt.getDate()).padStart(2, '0');
            var mm = String(dt.getMonth() + 1).padStart(2, '0');
            var yyyy = dt.getFullYear();
            return dd + '/' + mm + '/' + yyyy;
          };
          var y2Date = new Date(year + 1, month, day);
          var y3Date = new Date(year + 2, month, day);
          pricing.step_y1_start = fmtDate(baseDate);
          pricing.step_y2_start = fmtDate(y2Date);
          pricing.step_y3_start = fmtDate(y3Date);
          pricing.smooth_24_start = fmtDate(baseDate);
          pricing.smooth_36_start = fmtDate(baseDate);
        }
      }
    }

    // Build env charges
    var env_charges = {};
    if (appliedRates && appliedRates.env) {
      var lrec = appliedRates.env.LREC || {};
      var srec = appliedRates.env.SREC || {};
      var veec = appliedRates.env.VEEC || {};
      var esc = appliedRates.env.ESC || {};
      env_charges.lgcs_cert = [lrec.CAL26 != null ? String(lrec.CAL26) : '', lrec.CAL27 != null ? String(lrec.CAL27) : '', lrec.CAL28 != null ? String(lrec.CAL28) : '', ''];
      env_charges.stcs_cert = [srec.CAL26 != null ? String(srec.CAL26) : '', srec.CAL27 != null ? String(srec.CAL27) : '', srec.CAL28 != null ? String(srec.CAL28) : '', ''];
      if (state === 'VIC') {
        env_charges.state_scheme_cert = [veec.CAL26 != null ? String(veec.CAL26) : '', veec.CAL27 != null ? String(veec.CAL27) : '', veec.CAL28 != null ? String(veec.CAL28) : '', ''];
      } else if (state === 'NSW') {
        env_charges.state_scheme_cert = [esc.CAL26 != null ? String(esc.CAL26) : '', esc.CAL27 != null ? String(esc.CAL27) : '', esc.CAL28 != null ? String(esc.CAL28) : '', ''];
      }
    }

    // Build other charges — default service fee $80/month for C&I
    var other_charges = { service_fee_monthly: '80' };
    if (appliedRates) {
      if (appliedRates.service_charge != null) other_charges.service_fee_monthly = String(appliedRates.service_charge);
      var meterVal = (appliedRates.metering || {})['COMMS4'] || (appliedRates.metering || {})['COMMS3'];
      if (meterVal != null && typeof meterVal === 'number') other_charges.metering_charge_monthly = String(meterVal);
    }
    if (d.meter_fee_pa && !other_charges.metering_charge_monthly) {
      other_charges.metering_charge_monthly = String(Math.round(parseFloat(d.meter_fee_pa) / 12));
    }

    var payload = {
      contract_state: state,
      company_name: d.company_name || '',
      trading_name: d.trading_name || '',
      abn: d.abn || '',
      acn: d.acn || '',
      contact_name: d.contact_name || '',
      phone: d.phone || '',
      mobile: d.mobile || '',
      accounts_email: d.email || '',
      billing_address: d.billing_address || '',
      billing_suburb: d.billing_suburb || '',
      billing_state: d.billing_state || '',
      billing_postcode: d.billing_postcode || '',
      connection_type: d.connection_type || 'transfer',
      proposed_transfer_date: d.proposed_transfer_date || '',
      contract_term_months: '',
      supply_addresses: (d.supply_addresses || []).map(function(a) {
        return { supply_address: a.supply_address || '', suburb: a.suburb || '', state: a.state || '', postcode: a.postcode || '', nmi: a.nmi || '', estimated_pk: a.estimated_pk || '', estimated_opk: a.estimated_opk || '', estimated_shoulder: a.estimated_shoulder || '', estimated_kwh: a.estimated_kwh || '', tariff: a.tariff || '' };
      }),
      pricing: pricing,
      env_charges: env_charges,
      other_charges: other_charges,
      commission_type: commType || '',
      commission_value: commValue || '',
      status: 'draft'
    };
    axios.post(API_URL + '/api/contracts?authorization=' + token, payload, { headers: { 'Content-Type': 'application/json' } })
      .then(function(r) {
        toast.success('C&I Contract created');
        navigate('/contracts', { state: { editContractId: r.data.id } });
      })
      .catch(function(err) { toast.error(err.response?.data?.detail || 'Failed to create contract'); })
      .finally(function() { setCreating(false); });
  }

  function fmtRate(v) {
    if (v == null || v === '' || v === 0) return '-';
    return typeof v === 'number' ? v.toFixed(4) : String(v);
  }

  function fmtRateWithComm(v) {
    if (v == null || v === '' || v === 0) return '-';
    var base = typeof v === 'number' ? v : parseFloat(v);
    if (isNaN(base)) return String(v);
    var final_val = applyCommission(base);
    if (final_val === base || !commValue || parseFloat(commValue) === 0) return base.toFixed(4);
    return (<span>{base.toFixed(4)} <span className="text-emerald-600 font-medium">&rarr; {final_val.toFixed(4)}</span></span>);
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Email to C&I Quote</h1>
          <p className="text-slate-500 text-sm mt-1">Paste a tender request email and auto-generate a C&I contract</p>
        </div>

        {!extracted ? (
          <div className="grid grid-cols-1 gap-6">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />Paste Email Content</CardTitle><CardDescription>Paste the full email or tender request below</CardDescription></CardHeader>
              <CardContent>
                <textarea
                  className="w-full min-h-[300px] p-4 border rounded-lg font-mono text-sm bg-slate-50 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-y"
                  placeholder={"Paste the tender request email here...\n\nExample:\nCustomer name: ACME PTY LTD\nABN: 12 345 678 901\nNMI: 4102016712\nPeak (kWh): 24,766\n..."}
                  value={emailText}
                  onChange={function(e) { setEmailText(e.target.value); }}
                  data-testid="email-paste-input"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5" />Attachment (Optional)</CardTitle><CardDescription>Upload a CSV or Excel file with site/usage details</CardDescription></CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <Input type="file" accept=".csv,.xlsx,.xls,.pdf,.png,.jpg,.jpeg" onChange={function(e) { setFile(e.target.files[0] || null); }} data-testid="email-file-upload" />
                  {file && <Badge variant="outline" className="text-xs">{file.name}</Badge>}
                </div>
              </CardContent>
            </Card>

            <Button onClick={extract} disabled={extracting || (!emailText.trim() && !file)} className="w-full py-6 text-base" data-testid="extract-btn">
              {extracting ? <><Loader2 className="w-5 h-5 animate-spin mr-2" />Extracting with AI...</> : <><Sparkles className="w-5 h-5 mr-2" />Extract Contract Details</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Building2 className="w-5 h-5" />Extracted Details</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={function() { setExtracted(null); setAppliedRates(null); }} data-testid="back-to-paste-btn">Start Over</Button>
                <Button onClick={createContract} disabled={creating} data-testid="create-contract-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}Create C&I Contract
                </Button>
              </div>
            </div>

            {/* Rate Card + Broker Commission Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border-primary/20 bg-primary/[0.02]">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><DollarSign className="w-4 h-4" />Apply Rate Card</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Rate Card</Label>
                      <Select value={selectedCard || ''} onValueChange={loadRateCard}>
                        <SelectTrigger className="h-8 bg-white text-sm" data-testid="eq-rate-card-select"><SelectValue placeholder="Select rate card..." /></SelectTrigger>
                        <SelectContent>
                          {rateCards.map(function(c) { return <SelectItem key={c.id} value={c.id}>{c.filename}</SelectItem>; })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Quarter</Label>
                      <div className="flex gap-1">
                        {['Q1','Q2','Q3','Q4'].map(function(q) {
                          return <Button key={q} variant={selectedQuarter === q ? 'default' : 'outline'} size="sm" className="flex-1 h-8 text-xs" onClick={function() { setSelectedQuarter(q); }}>{q}</Button>;
                        })}
                      </div>
                    </div>
                  </div>
                  <Button size="sm" onClick={applyRates} disabled={!rateCardDetail} className="w-full" data-testid="eq-apply-rates-btn">Apply Rates</Button>
                </CardContent>
              </Card>

              <Card className="border-amber-200 bg-amber-50/30">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><DollarSign className="w-4 h-4 text-amber-600" />Broker Commission</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <div className="flex gap-1">
                        <Button variant={commType === 'percent' ? 'default' : 'outline'} size="sm" className="flex-1 h-8 text-xs" onClick={function() { setCommType('percent'); }} data-testid="comm-type-percent">% of Rate</Button>
                        <Button variant={commType === 'ckwh' ? 'default' : 'outline'} size="sm" className="flex-1 h-8 text-xs" onClick={function() { setCommType('ckwh'); }} data-testid="comm-type-ckwh">c/kWh Flat</Button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">{commType === 'percent' ? 'Commission %' : 'c/kWh to add'}</Label>
                      <Input type="number" step="0.01" value={commValue} onChange={function(e) { setCommValue(e.target.value); }} placeholder={commType === 'percent' ? 'e.g. 3' : 'e.g. 0.25'} className="h-8 text-sm" data-testid="comm-value" />
                    </div>
                  </div>
                  {commValue && parseFloat(commValue) > 0 && (
                    <p className="text-xs text-amber-700">
                      {commType === 'percent'
                        ? 'e.g. 17c/kWh + ' + commValue + '% = ' + (17 + 17 * parseFloat(commValue) / 100).toFixed(4) + 'c/kWh'
                        : 'e.g. 17c/kWh + ' + commValue + 'c = ' + (17 + parseFloat(commValue)).toFixed(4) + 'c/kWh'}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Applied Rates Display */}
            {appliedRates && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Energy Rates — {appliedRates.stateKey} {commValue && parseFloat(commValue) > 0 ? <Badge className="ml-2 bg-amber-100 text-amber-800 text-xs">{commType === 'percent' ? commValue + '% commission' : commValue + 'c/kWh commission'}</Badge> : null}</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="p-2 text-xs font-medium text-slate-500">Type</th>
                          <th className="p-2 text-xs font-medium text-slate-500">Year</th>
                          <th className="p-2 text-xs font-medium text-slate-500">Peak (c/kWh)</th>
                          <th className="p-2 text-xs font-medium text-slate-500">Shoulder (c/kWh)</th>
                          <th className="p-2 text-xs font-medium text-slate-500">Off-Peak (c/kWh)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(appliedRates.stepped || {}).map(function(y) {
                          var r = appliedRates.stepped[y];
                          return (
                            <tr key={'st' + y} className="border-b border-slate-100">
                              <td className="p-2 text-xs font-medium">Stepped</td>
                              <td className="p-2 text-xs">Year {y}</td>
                              <td className="p-2 text-xs">{fmtRateWithComm(r.peak)}</td>
                              <td className="p-2 text-xs">{fmtRateWithComm(r.shoulder)}</td>
                              <td className="p-2 text-xs">{fmtRateWithComm(r.offpeak)}</td>
                            </tr>
                          );
                        })}
                        {Object.keys(appliedRates.smoothed || {}).map(function(y) {
                          var r = appliedRates.smoothed[y];
                          return (
                            <tr key={'sm' + y} className="border-b border-slate-100">
                              <td className="p-2 text-xs font-medium">Smoothed</td>
                              <td className="p-2 text-xs">{y === '2' ? '24mo' : '36mo'}</td>
                              <td className="p-2 text-xs">{fmtRateWithComm(r.peak)}</td>
                              <td className="p-2 text-xs">{fmtRateWithComm(r.shoulder)}</td>
                              <td className="p-2 text-xs">{fmtRateWithComm(r.offpeak)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader><CardTitle className="text-base">Customer Details</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label className="text-xs">Company Name</Label><Input value={extracted.company_name || ''} onChange={function(e) { updateField('company_name', e.target.value); }} data-testid="eq-company-name" /></div>
                <div><Label className="text-xs">Trading Name</Label><Input value={extracted.trading_name || ''} onChange={function(e) { updateField('trading_name', e.target.value); }} /></div>
                <div><Label className="text-xs">ABN</Label><Input value={extracted.abn || ''} onChange={function(e) { updateField('abn', e.target.value); }} data-testid="eq-abn" /></div>
                <div><Label className="text-xs">ACN</Label><Input value={extracted.acn || ''} onChange={function(e) { updateField('acn', e.target.value); }} /></div>
                <div><Label className="text-xs">Contact Name</Label><Input value={extracted.contact_name || ''} onChange={function(e) { updateField('contact_name', e.target.value); }} data-testid="eq-contact-name" /></div>
                <div><Label className="text-xs">Phone</Label><Input value={extracted.phone || ''} onChange={function(e) { updateField('phone', e.target.value); }} /></div>
                <div><Label className="text-xs">Mobile</Label><Input value={extracted.mobile || ''} onChange={function(e) { updateField('mobile', e.target.value); }} /></div>
                <div><Label className="text-xs">Email</Label><Input value={extracted.email || ''} onChange={function(e) { updateField('email', e.target.value); }} data-testid="eq-email" /></div>
                <div><Label className="text-xs">State</Label>
                  <select className="w-full h-9 px-3 border rounded-md text-sm" value={extracted.contract_state || 'QLD_SA'} onChange={function(e) { updateField('contract_state', e.target.value); setAppliedRates(null); }} data-testid="eq-state">
                    <option value="NSW">NSW</option><option value="VIC">VIC</option><option value="QLD_SA">QLD / SA</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Billing Address</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="col-span-2"><Label className="text-xs">Address</Label><Input value={extracted.billing_address || ''} onChange={function(e) { updateField('billing_address', e.target.value); }} /></div>
                <div><Label className="text-xs">Suburb</Label><Input value={extracted.billing_suburb || ''} onChange={function(e) { updateField('billing_suburb', e.target.value); }} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">State</Label><Input value={extracted.billing_state || ''} onChange={function(e) { updateField('billing_state', e.target.value); }} /></div>
                  <div><Label className="text-xs">Postcode</Label><Input value={extracted.billing_postcode || ''} onChange={function(e) { updateField('billing_postcode', e.target.value); }} /></div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Contract Details</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Connection Type</Label>
                  <select className="w-full h-9 px-3 border rounded-md text-sm" value={extracted.connection_type || 'transfer'} onChange={function(e) { updateField('connection_type', e.target.value); }}>
                    <option value="transfer">Transfer</option><option value="move_in">Move In</option><option value="renewal">Renewal</option>
                  </select>
                </div>
                <div><Label className="text-xs">Start Date</Label><Input value={extracted.proposed_transfer_date || ''} onChange={function(e) { updateField('proposed_transfer_date', e.target.value); }} placeholder="DD/MM/YYYY" /></div>
                <div><Label className="text-xs">Term (months)</Label><Input value={extracted.contract_term_months || ''} onChange={function(e) { updateField('contract_term_months', e.target.value); }} /></div>
                <div><Label className="text-xs">Term Options</Label><Input value={extracted.contract_term_options || ''} className="bg-slate-50" readOnly /></div>
                <div><Label className="text-xs">Pricing Type</Label><Input value={extracted.pricing_type || ''} onChange={function(e) { updateField('pricing_type', e.target.value); }} /></div>
                <div><Label className="text-xs">Current Supplier</Label><Input value={extracted.current_supplier || ''} onChange={function(e) { updateField('current_supplier', e.target.value); }} /></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Supply Sites / NMIs</CardTitle>
                  <Button variant="outline" size="sm" onClick={addAddress}><Plus className="w-4 h-4 mr-1" />Add Site</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {(extracted.supply_addresses || []).map(function(addr, idx) {
                  return (
                    <div key={idx} className="border rounded-lg p-3 space-y-2 bg-slate-50/50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-slate-500">Site {idx + 1}</span>
                        {(extracted.supply_addresses || []).length > 1 && <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={function() { removeAddress(idx); }}><Trash2 className="w-3 h-3" /></Button>}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div className="col-span-2"><Label className="text-xs">Address</Label><Input value={addr.supply_address || ''} onChange={function(e) { updateAddress(idx, 'supply_address', e.target.value); }} className="h-8 text-sm" /></div>
                        <div><Label className="text-xs">Suburb</Label><Input value={addr.suburb || ''} onChange={function(e) { updateAddress(idx, 'suburb', e.target.value); }} className="h-8 text-sm" /></div>
                        <div className="grid grid-cols-2 gap-1">
                          <div><Label className="text-xs">State</Label><Input value={addr.state || ''} onChange={function(e) { updateAddress(idx, 'state', e.target.value); }} className="h-8 text-sm" /></div>
                          <div><Label className="text-xs">Post</Label><Input value={addr.postcode || ''} onChange={function(e) { updateAddress(idx, 'postcode', e.target.value); }} className="h-8 text-sm" /></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div><Label className="text-xs">NMI</Label><Input value={addr.nmi || ''} onChange={function(e) { updateAddress(idx, 'nmi', e.target.value); }} className="h-8 text-sm font-mono" data-testid={'eq-nmi-' + idx} /></div>
                        <div><Label className="text-xs">Peak kWh</Label><Input value={addr.estimated_pk || ''} onChange={function(e) { updateAddress(idx, 'estimated_pk', e.target.value); }} className="h-8 text-sm" /></div>
                        <div><Label className="text-xs">Off-Peak kWh</Label><Input value={addr.estimated_opk || ''} onChange={function(e) { updateAddress(idx, 'estimated_opk', e.target.value); }} className="h-8 text-sm" /></div>
                        <div><Label className="text-xs">Shoulder kWh</Label><Input value={addr.estimated_shoulder || ''} onChange={function(e) { updateAddress(idx, 'estimated_shoulder', e.target.value); }} className="h-8 text-sm" /></div>
                        <div><Label className="text-xs">Total kWh</Label><Input value={addr.estimated_kwh || ''} onChange={function(e) { updateAddress(idx, 'estimated_kwh', e.target.value); }} className="h-8 text-sm" /></div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {(extracted.broker_name || extracted.tender_agent || extracted.tender_close_date || extracted.notes) && (
              <Card>
                <CardHeader><CardTitle className="text-base">Broker / Tender Info</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div><Label className="text-xs">Broker</Label><Input value={extracted.broker_name || ''} onChange={function(e) { updateField('broker_name', e.target.value); }} /></div>
                  <div><Label className="text-xs">Broker Phone</Label><Input value={extracted.broker_phone || ''} onChange={function(e) { updateField('broker_phone', e.target.value); }} /></div>
                  <div><Label className="text-xs">Tender Agent</Label><Input value={extracted.tender_agent || ''} onChange={function(e) { updateField('tender_agent', e.target.value); }} /></div>
                  <div><Label className="text-xs">Close Date</Label><Input value={extracted.tender_close_date || ''} onChange={function(e) { updateField('tender_close_date', e.target.value); }} /></div>
                  {extracted.notes && <div className="col-span-2 md:col-span-4"><Label className="text-xs">Notes</Label><textarea className="w-full p-2 border rounded-md text-sm min-h-[60px]" value={extracted.notes || ''} onChange={function(e) { updateField('notes', e.target.value); }} /></div>}
                </CardContent>
              </Card>
            )}

            <div className="flex gap-3 justify-end pb-8">
              <Button variant="outline" onClick={function() { setExtracted(null); setAppliedRates(null); }}>Start Over</Button>
              <Button onClick={createContract} disabled={creating} className="px-8" data-testid="create-contract-btn-bottom">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}Create C&I Contract
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
